import { useCallback, useState } from 'react'
import InputField from '../../../components/InputField'
import ApiRoute from '../../../components/ApiRoute'

const Providers = ({ activeSession, callApiFn, isApiCalling }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [zipCode, setZipCode] = useState(activeSession?.contact?.zip || '')
  const [searchResults, setSearchResults] = useState(null)
  const [stagedProviders, setStagedProviders] = useState([])
  const [removedIds, setRemovedIds] = useState([])
  const hasContact = Boolean(activeSession?.contact?.id)
  const hasUnsavedChanges = stagedProviders.length > 0 || removedIds.length > 0

  const search = useCallback(async () => {
    if (!searchTerm.trim() || !zipCode.trim()) return
    setSearchResults(null)
    const { body } = await callApiFn({
      method: 'GET',
      route: `providers/${zipCode}/${searchTerm}`
    })
    setSearchResults(body || [])
  }, [searchTerm, zipCode, callApiFn])

  const contactProvidersGet = () => {
    callApiFn({
      method: 'GET',
      route: 'contact/{id}/providers',
      saveSuccessPath: 'providers'
    })
  }

  const selectProvider = (provider) => {
    setStagedProviders([...stagedProviders, provider])
  }

  const removeFromStaged = (id) => {
    setStagedProviders(stagedProviders.filter(p => p.id !== id))
  }

  const removeFromCurrent = (id) => {
    setRemovedIds([...removedIds, id])
  }

  const undoRemove = (id) => {
    setRemovedIds(removedIds.filter(i => i !== id))
  }

  const putProviders = useCallback(async () => {
    const existing = (activeSession?.providers || []).filter(p => !removedIds.includes(p.id))
    const payload = [
      ...existing.map(p => ({ id: p.id })),
      ...stagedProviders.map(p => ({ id: p.id }))
    ]
    await callApiFn({
      method: 'PUT',
      route: 'contact/{id}/providers',
      body: payload,
      saveSuccessPath: 'providers'
    })
    setStagedProviders([])
    setRemovedIds([])
  }, [activeSession, stagedProviders, removedIds, callApiFn])

  return (
    <div>
      <ApiRoute method='GET' route='/contact/{contactId}/providers' onClick={contactProvidersGet} disabled={isApiCalling || !hasContact} />

      {!hasContact && (
        <div className='bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded my-2'>
          No contact selected. Create a contact first.
        </div>
      )}

      {/* Current contact providers */}
      {hasContact && (activeSession?.providers || []).length > 0 && (
        <div className='mb-3'>
          <h4 className='font-semibold mb-1'>Current Providers</h4>
          <table className='w-full text-sm border border-gray-300'>
            <thead className='bg-gray-100'>
              <tr>
                <th className='text-left p-1 border-b'>Name</th>
                <th className='text-left p-1 border-b'>Specialty</th>
                <th className='p-1 border-b' />
              </tr>
            </thead>
            <tbody>
              {(activeSession?.providers || []).map((p) => {
                const isRemoved = removedIds.includes(p.id)
                return (
                  <tr key={p.id} className={`border-b ${isRemoved ? 'bg-red-50 line-through text-gray-400' : ''}`}>
                    <td className='p-1'>{p.presentation_name || p.presentationName || '-'}</td>
                    <td className='p-1'>{p.specialty || '-'}</td>
                    <td className='p-1'>
                      {isRemoved
                        ? <button className='text-blue-600 text-xs hover:underline' onClick={() => undoRemove(p.id)}>Undo</button>
                        : <button className='text-red-600 text-xs hover:underline' onClick={() => removeFromCurrent(p.id)}>Remove</button>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Unsaved changes */}
      {hasUnsavedChanges && (
        <div className='mb-3 border-2 border-orange-400 rounded p-2 bg-orange-50'>
          <h4 className='font-semibold mb-1'>Unsaved Changes</h4>
          {removedIds.length > 0 && <div className='text-sm text-red-600 mb-1'>{removedIds.length} provider(s) marked for removal</div>}
          {stagedProviders.length > 0 && (
            <div className='mb-2'>
              <div className='text-sm text-green-600 mb-1'>{stagedProviders.length} provider(s) staged to add:</div>
              {stagedProviders.map((p) => (
                <div key={p.id} className='flex justify-between items-center text-sm border-b py-1'>
                  <span>{p.presentation_name || p.presentationName || p.id} - {p.specialty || ''}</span>
                  <button className='text-red-600 text-xs hover:underline' onClick={() => removeFromStaged(p.id)}>Remove</button>
                </div>
              ))}
            </div>
          )}
          <ApiRoute method='PUT' route='/contact/{contactId}/providers' onClick={putProviders} disabled={isApiCalling || !hasContact} />
        </div>
      )}

      {/* Search */}
      <h4 className='font-semibold mt-4 mb-1'>Search Providers</h4>
      <div className='flex gap-2 items-end mb-2'>
        <InputField labelFor='providerZip' type='text' id='providerZip' name='providerZip' onChange={(e) => setZipCode(e.target.value)} value={zipCode} containerClassName='w-28'>Zip Code</InputField>
        <InputField labelFor='providerSearch' type='text' id='providerSearch' name='providerSearch' onChange={(e) => setSearchTerm(e.target.value)} value={searchTerm} containerClassName='flex-1'>Search Term</InputField>
        <ApiRoute method='GET' route='/providers/{zipCode}/{term}' onClick={search} disabled={isApiCalling || !searchTerm.trim() || !zipCode.trim()} />
      </div>

      {searchResults !== null && searchResults.length === 0 && (
        <div className='text-gray-500 italic'>No providers found for "{searchTerm}"</div>
      )}

      {searchResults !== null && searchResults.length > 0 && (
        <div>
          <div className='text-sm text-gray-600 mb-1'>Found {searchResults.length} result(s)</div>
          <table className='w-full text-sm border border-gray-300'>
            <thead className='bg-gray-100'>
              <tr>
                <th className='text-left p-1 border-b'>Name</th>
                <th className='text-left p-1 border-b'>Specialty</th>
                <th className='p-1 border-b' />
              </tr>
            </thead>
            <tbody>
              {searchResults.map((p) => (
                <tr key={p.id} className='border-b hover:bg-gray-50'>
                  <td className='p-1'>{p.presentation_name || p.presentationName || '-'}</td>
                  <td className='p-1'>{p.specialty || '-'}</td>
                  <td className='p-1'>
                    <button
                      className='text-blue-600 text-xs hover:underline disabled:text-gray-400'
                      onClick={() => selectProvider(p)}
                      disabled={!hasContact || isApiCalling}
                    >
                      Select
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default Providers
